/**
 * API методы для работы с переопределением прав пользователей
 * Дата: 31.01.2026 - Создан для user_permissions
 * Связан с: migrations/system/000010_user_permissions.up.sql
 */
import { apiClient } from './client';

// Доступное право
export interface Permission {
  id: string;
  resource: string;
  action: string;
  description: string;
}

// Переопределение права для пользователя
export interface UserPermission {
  permission_id: string;
  resource: string;
  action: string;
  description: string;
  granted: boolean;  // true = добавлено, false = отозвано
  reason?: string;
  created_at: string;
}

// Эффективные права пользователя (роль + переопределения)
export interface EffectivePermissions {
  user_id: string;
  role_permissions: string[];      // Права от роли
  user_overrides: UserPermission[]; // Переопределения
  effective: string[];              // Итоговые права
}

// IP в whitelist
export interface UserIPWhitelist {
  id: string;
  user_id: string;
  ip_address: string;
  description?: string;
  is_active: boolean;
  created_at: string;
}

// Tenant IP whitelist
export interface TenantIPWhitelist {
  id: string;
  tenant_id: string;
  ip_address: string;
  description?: string;
  is_active: boolean;
  created_at: string;
}

export const userPermissionsApi = {
  // Получить все доступные права
  listAllPermissions: async (): Promise<Permission[]> => {
    const response = await apiClient.get('/permissions');
    return response.data.permissions || response.data;
  },

  // Получить эффективные права пользователя
  getEffectivePermissions: async (userId: string): Promise<EffectivePermissions> => {
    const response = await apiClient.get(`/users/${userId}/permissions`);
    return response.data;
  },

  // Получить переопределения прав пользователя
  getUserOverrides: async (userId: string): Promise<UserPermission[]> => {
    const response = await apiClient.get(`/users/${userId}/permissions/overrides`);
    return response.data.overrides || [];
  },

  // Добавить/изменить переопределение права
  setPermissionOverride: async (
    userId: string, 
    permissionId: string, 
    granted: boolean,
    reason?: string
  ): Promise<void> => {
    await apiClient.post(`/users/${userId}/permissions/overrides`, {
      permission_id: permissionId,
      granted,
      reason
    });
  },

  // Удалить переопределение (вернуть к роли)
  removePermissionOverride: async (userId: string, permissionId: string): Promise<void> => {
    await apiClient.delete(`/users/${userId}/permissions/overrides/${permissionId}`);
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // IP Whitelist для пользователя
  // ═══════════════════════════════════════════════════════════════════════════

  // Получить IP whitelist пользователя
  getUserIPWhitelist: async (userId: string): Promise<UserIPWhitelist[]> => {
    const response = await apiClient.get(`/users/${userId}/ip-whitelist`);
    return response.data.ips || [];
  },

  // Добавить IP в whitelist пользователя
  addUserIP: async (userId: string, ip: string, description?: string): Promise<UserIPWhitelist> => {
    const response = await apiClient.post(`/users/${userId}/ip-whitelist`, {
      ip_address: ip,
      description
    });
    return response.data;
  },

  // Удалить IP из whitelist пользователя
  removeUserIP: async (userId: string, ipId: string): Promise<void> => {
    await apiClient.delete(`/users/${userId}/ip-whitelist/${ipId}`);
  },

  // Переключить активность IP
  toggleUserIP: async (userId: string, ipId: string, isActive: boolean): Promise<void> => {
    await apiClient.patch(`/users/${userId}/ip-whitelist/${ipId}`, { is_active: isActive });
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // IP Whitelist для tenant (admin only)
  // ═══════════════════════════════════════════════════════════════════════════

  // Получить IP whitelist tenant
  getTenantIPWhitelist: async (tenantId: string): Promise<TenantIPWhitelist[]> => {
    const response = await apiClient.get(`/tenants/${tenantId}/ip-whitelist`);
    return response.data.ips || [];
  },

  // Добавить IP в whitelist tenant
  addTenantIP: async (tenantId: string, ip: string, description?: string): Promise<TenantIPWhitelist> => {
    const response = await apiClient.post(`/tenants/${tenantId}/ip-whitelist`, {
      ip_address: ip,
      description
    });
    return response.data;
  },

  // Удалить IP из whitelist tenant
  removeTenantIP: async (tenantId: string, ipId: string): Promise<void> => {
    await apiClient.delete(`/tenants/${tenantId}/ip-whitelist/${ipId}`);
  },

  // Включить/выключить IP whitelist для tenant
  toggleTenantIPWhitelist: async (tenantId: string, enabled: boolean): Promise<void> => {
    await apiClient.patch(`/tenants/${tenantId}/settings`, { ip_whitelist_enabled: enabled });
  },
};
